<template>
    <span
        ref="spanElement"
        class="tooltipcomp"
        data-bs-toggle="tooltip"
        data-bs-offset="20, 10"
        data-bs-html="true"
        title="..."
        @mouseenter="mouseEnter"
        @mouseleave="mouseLeave"
        v-on="{ 'show.bs.tooltip': setTooltipContent }"
    >
        <slot></slot>
        <Teleport
            to="body"
            v-if="$slots.tooltipcontent"
        >
            <div
                class="d-none"
                ref="tooltipcontentdiv"
            >
                <slot name="tooltipcontent"></slot>
            </div>
        </Teleport>
    </span>
</template>
<script setup lang="ts">
import { $tm } from '@/locize';
import { isLocalHost } from '@/utilities/utils';
import { Tooltip } from 'bootstrap';
import { useSlots } from 'vue';
import { onMounted, onUnmounted, ref, Teleport } from 'vue';

const mouseEnter = () => {
    if (props.trigger == 'hover') {
        tooltipInstance?.show();
    }
};

const mouseLeave = () => {
    if (props.trigger == 'hover') {
        tooltipInstance?.hide();
    }
};

const props = withDefaults(
    defineProps<{
        /**
         * The text that will be shown in the tooltip
         */
        text?: string | string[];

        /**
         * The placement of the tooltip
         * @default 'auto'
         */
        placement?: 'auto' | 'top' | 'bottom' | 'left' | 'right';

        /**
         * The trigger of the tooltip
         * @default 'hover'
         */
        trigger?: 'hover' | 'manual' | 'show' | 'click';

        /**
         * The type of the tooltip
         * @default undefined
         */
        type?: 'success' | 'info' | 'warning' | 'danger';
    }>(),
    {
        placement: 'auto',
        trigger: 'hover',
    },
);

const slots = useSlots();

// The html element that will contain the tooltip
const spanElement = ref<HTMLElement | null>(null);

// The tooltip instance
let tooltipInstance: Tooltip | null = null;

let autocloseTimeout: number | null = null;

const tooltipcontentdiv = ref<HTMLElement | null>(null);

// Warn about empty tooltips in dev
if (isLocalHost()) {
    if (!slots.tooltipcontent && !props.text) {
        console.warn('Tooltip does not have text or element. This tooltip is useless');
    }
}

const getTranslatedContent = () => {
    // Try to get the content from the tooltipcontent slot first
    let slotContent = slots.tooltipcontent ? (tooltipcontentdiv.value?.innerHTML ?? '') : '';

    if (slotContent) {
        return slotContent;
    }

    if (!props.text) {
        return '';
    }

    // Fallback to the supplied text
    if (Array.isArray(props.text)) {
        return props.text.map((text) => $tm(text, text, true)).join('');
    } else {
        return $tm(props.text, props.text, true);
    }
};

/**
 * Set the content of the tooltip. We use this helper function to translate the text with commonmark.
 * @param e
 */
const setTooltipContent = () => {
    spanElement.value
        ? tooltipInstance?.setContent({
              '.tooltip-inner': getTranslatedContent(),
          })
        : null;

    if (props.trigger == 'click') {
        if (autocloseTimeout) {
            window.clearTimeout(autocloseTimeout);
            autocloseTimeout = null;
        }

        // We automatically hide the tooltip after 10 seconds
        autocloseTimeout = window.setTimeout(() => {
            tooltipInstance?.hide();
        }, 10000);
    }
};

onMounted(() => {
    if (spanElement.value) {
        tooltipInstance = Tooltip.getOrCreateInstance(spanElement.value, {
            placement: props.placement,
            trigger: props.trigger,
            customClass: props.type ?? '',
        });

        // We always show the tooltip if it is set
        // TODO: I think we will do this a different way in the future
        if (props.trigger == 'show') {
            window.setTimeout(() => {
                tooltipInstance?.show();
            }, 100);
        }
    }
});

onUnmounted(() => {
    tooltipInstance?.dispose();
});
</script>
